import { create } from 'zustand'
import { Skill } from '../types'
import { genId, nowIso } from '../utils/format'
import { useTaskStore } from './taskStore'

interface GenerateInput {
  prompt: string
  nameHint: string
  modelId: string
  enableAfterInstall: boolean
}

interface UrlInstallInput {
  url: string
  expectedSha256?: string
  enableAfterInstall: boolean
}

interface SkillStore {
  skills: Skill[]
  toggleSkill: (id: string) => void
  deleteSkill: (id: string) => boolean
  installFromUpload: (fileName: string, enableAfter: boolean) => string
  generateWithAi: (input: GenerateInput) => string
  installFromUrl: (input: UrlInstallInput) => string
}

const seed: Skill[] = [
  {
    skillId: 'skill_digital_human',
    name: '数字人口播生成 Skill',
    description: '根据文案脚本生成数字人口播视频，支持音色、形象与字幕配置',
    version: '1.2.0',
    source: 'third_party',
    status: 'enabled',
    author: 'MetaHuman Studio',
    homepage: 'https://example.com/skills/digital-human',
    downloadUrl: 'https://example.com/skills/digital-human.zip',
    tags: ['video', 'digital-human', 'tts', 'marketing'],
    capabilities: [
      'script_to_video',
      'voice_clone',
      'avatar_select',
      'subtitle_generate',
    ],
    readme:
      '# 数字人口播生成 Skill\n\n输入一段文案脚本，自动生成数字人口播视频。\n\n## 能力\n- 文案脚本转视频（script_to_video）\n- 多音色 TTS 与音色克隆\n- 多种数字人形象选择\n- 自动字幕生成\n\n## 输入参数\n- `script`：口播文案\n- `avatarId`：数字人形象\n- `voice`：音色\n- `resolution`：分辨率（默认 1080p）\n\n## 输出\n- 视频下载链接\n- 时长、分辨率、字幕文件',
    dependencies: ['ffmpeg', 'tts-engine>=2.1', 'avatar-render'],
    usedByAgentCount: 1,
    createdAt: '2026-05-30T10:00:00Z',
    updatedAt: '2026-06-04T15:30:00Z',
  },
  {
    skillId: 'skill_ops_linux',
    name: 'Linux 运维 Skill',
    description: '支持服务器状态检查、日志分析和常见故障排查',
    version: '1.0.0',
    source: 'third_party',
    status: 'enabled',
    author: 'example',
    homepage: 'https://example.com/skills/linux-ops',
    downloadUrl: 'https://example.com/skills/linux-ops.zip',
    tags: ['ops', 'linux'],
    capabilities: ['server_check', 'log_analysis', 'troubleshoot'],
    readme:
      '# Linux 运维 Skill\n\n提供服务器状态检查、日志分析与常见故障排查能力。\n\n## 能力\n- 服务器健康检查\n- Nginx / 系统日志分析\n- 故障定位建议',
    dependencies: ['paramiko>=3.0', 'psutil'],
    usedByAgentCount: 3,
    createdAt: '2026-06-01T12:00:00Z',
    updatedAt: '2026-06-04T10:00:00Z',
  },
  {
    skillId: 'skill_nginx_log',
    name: 'Nginx 日志分析 Skill',
    description: '解析 Nginx 访问日志并生成异常访问报告',
    version: '0.3.2',
    source: 'ai_generated',
    status: 'enabled',
    author: 'Skill Builder Agent',
    tags: ['log', 'nginx', 'security'],
    capabilities: ['parse_access_log', 'anomaly_report'],
    readme:
      '# Nginx 日志分析 Skill\n\n由 AI 自动生成，用于分析 Nginx 访问日志。\n\n## 输出\n- 异常访问 IP Top N\n- 状态码分布\n- 可疑请求路径',
    dependencies: ['pandas'],
    usedByAgentCount: 1,
    createdAt: '2026-06-03T09:00:00Z',
    updatedAt: '2026-06-03T09:40:00Z',
  },
  {
    skillId: 'skill_code_review',
    name: '代码审查 Skill',
    description: '对提交的代码进行静态分析与改进建议',
    version: '2.1.0',
    source: 'upload',
    status: 'disabled',
    author: 'team-dev',
    tags: ['code', 'review', 'dev'],
    capabilities: ['static_analysis', 'suggest_refactor'],
    readme: '# 代码审查 Skill\n\n对代码进行静态分析并给出改进建议。',
    dependencies: ['ruff', 'eslint'],
    usedByAgentCount: 0,
    createdAt: '2026-05-29T14:00:00Z',
    updatedAt: '2026-06-02T11:10:00Z',
  },
]

function finishInstall(skillId: string, enable: boolean) {
  useSkillStore.setState((s) => ({
    skills: s.skills.map((sk) =>
      sk.skillId === skillId
        ? {
            ...sk,
            status: enable ? 'enabled' : 'disabled',
            updatedAt: nowIso(),
          }
        : sk,
    ),
  }))
}

export const useSkillStore = create<SkillStore>((set, get) => ({
  skills: seed,
  toggleSkill: (id) =>
    set((s) => ({
      skills: s.skills.map((sk) =>
        sk.skillId === id
          ? {
              ...sk,
              status: sk.status === 'enabled' ? 'disabled' : 'enabled',
              updatedAt: nowIso(),
            }
          : sk,
      ),
    })),
  deleteSkill: (id) => {
    const sk = get().skills.find((x) => x.skillId === id)
    if (sk && sk.usedByAgentCount > 0) return false
    set((s) => ({ skills: s.skills.filter((x) => x.skillId !== id) }))
    return true
  },
  installFromUpload: (fileName, enableAfter) => {
    const skillId = genId('skill')
    const name = fileName.replace(/\.(zip|tar\.gz|tgz)$/i, '')
    set((s) => ({
      skills: [
        {
          skillId,
          name,
          description: '正在解压并校验上传的 Skill 压缩包…',
          version: '1.0.0',
          source: 'upload',
          status: 'installing',
          tags: ['upload'],
          capabilities: [],
          usedByAgentCount: 0,
          createdAt: nowIso(),
          updatedAt: nowIso(),
        },
        ...s.skills,
      ],
    }))
    const taskId = useTaskStore
      .getState()
      .createTask('skill_upload', `安装 Skill：${name}`, [
        '上传压缩包',
        '解压与路径校验',
        '校验 manifest',
        '注册到 Skills 中心',
      ])
    useTaskStore.getState().simulate(taskId, () => {
      finishInstall(skillId, enableAfter)
      useSkillStore.setState((s) => ({
        skills: s.skills.map((sk) =>
          sk.skillId === skillId
            ? {
                ...sk,
                description: `通过上传安装的 Skill：${name}`,
                capabilities: ['custom_skill'],
              }
            : sk,
        ),
      }))
    })
    return skillId
  },
  generateWithAi: (input) => {
    const skillId = genId('skill')
    set((s) => ({
      skills: [
        {
          skillId,
          name: input.nameHint || 'AI 生成的 Skill',
          description: input.prompt.slice(0, 80),
          version: '0.1.0',
          source: 'ai_generated',
          status: 'installing',
          author: 'Skill Builder Agent',
          tags: ['ai_generated'],
          capabilities: [],
          usedByAgentCount: 0,
          createdAt: nowIso(),
          updatedAt: nowIso(),
        },
        ...s.skills,
      ],
    }))
    const taskId = useTaskStore
      .getState()
      .createTask(
        'skill_generate',
        `AI 创建 Skill：${input.nameHint || 'Untitled'}`,
        [
          '理解需求提示词',
          '规划 Skill 结构',
          '生成代码与 manifest',
          '安装到 Skills 中心',
        ],
      )
    useTaskStore.getState().simulate(taskId, () => {
      finishInstall(skillId, input.enableAfterInstall)
      useSkillStore.setState((s) => ({
        skills: s.skills.map((sk) =>
          sk.skillId === skillId
            ? {
                ...sk,
                capabilities: ['ai_capability'],
                readme: `# ${sk.name}\n\n${input.prompt}`,
              }
            : sk,
        ),
      }))
    })
    return skillId
  },
  installFromUrl: (input) => {
    const skillId = genId('skill')
    const name =
      input.url
        .split('/')
        .pop()
        ?.replace(/\.(zip|tar\.gz)$/i, '') || '第三方 Skill'
    set((s) => ({
      skills: [
        {
          skillId,
          name,
          description: '正在从第三方链接下载并安装…',
          version: '1.0.0',
          source: 'third_party',
          status: 'installing',
          downloadUrl: input.url,
          tags: ['third_party'],
          capabilities: [],
          usedByAgentCount: 0,
          createdAt: nowIso(),
          updatedAt: nowIso(),
        },
        ...s.skills,
      ],
    }))
    const taskId = useTaskStore
      .getState()
      .createTask('skill_url_install', `安装第三方 Skill：${name}`, [
        '下载压缩包',
        '安全校验 (大小/类型/哈希)',
        '防路径穿越解压',
        '注册到 Skills 中心',
      ])
    useTaskStore.getState().simulate(taskId, () => {
      finishInstall(skillId, input.enableAfterInstall)
      useSkillStore.setState((s) => ({
        skills: s.skills.map((sk) =>
          sk.skillId === skillId
            ? {
                ...sk,
                description: `来自 ${input.url} 的第三方 Skill`,
                capabilities: ['third_party_skill'],
              }
            : sk,
        ),
      }))
    })
    return skillId
  },
}))
